import React, { useState, useEffect } from "react";
import MainModal from "./MainModal";
import "../../styles/search/detail/job-detail-info.css";


export default function BillingDataModal({ 
    isOpen, 
    onClose, 
    onCreateSimplified, 
    onCreateCustom,
    initialData = {} 
}) {
    const [formData, setFormData] = useState({
        fullName: '',
        contactID: '',
        address: '',
        city: '',
        postalCode: '',
        province: ''
    });

    const [isFormValid, setIsFormValid] = useState(false);

    // Estilos consistentes con HoldedInvoiceContainer
    const style = {
        container: {
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            padding: '20px',
            fontFamily: 'Arial, sans-serif'
        },
        title: {
            fontSize: 20,
            fontWeight: 'bold',
            marginBottom: 20,
            color: '#333'
        },
        formRow: {
            display: 'flex',
            flexDirection: 'column',
            marginBottom: 15
        },
        formRowDouble: {
            display: 'flex',
            flexDirection: 'row',
            gap: 15,
            marginBottom: 15
        },
        formColumn: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1
        },
        label: {
            fontSize: 14,
            fontWeight: 'bold',
            marginBottom: 5,
            color: '#555'
        },
        input: {
            padding: '8px 12px',
            fontSize: 14,
            border: '1px solid #ddd',
            borderRadius: 4,
            outline: 'none',
            transition: 'border-color 0.3s'
        },
        inputFocus: {
            borderColor: '#007bff'
        },
        buttonContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            marginTop: 20,
            paddingTop: 20,
            borderTop: '1px solid #eee'
        },
        button: {
            padding: '10px 20px',
            fontSize: 14,
            fontWeight: 'bold',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer',
            transition: 'background-color 0.3s'
        },
        buttonPrimary: {
            backgroundColor: '#007bff',
            color: 'white'
        },
        buttonSecondary: {
            backgroundColor: '#6c757d',
            color: 'white'
        },
        buttonDisabled: {
            backgroundColor: '#e9ecef',
            color: '#6c757d',
            cursor: 'not-allowed'
        },
        buttonHover: {
            opacity: 0.8
        }
    };

    // Efecto para cargar datos iniciales
    useEffect(() => {
        if (initialData && Object.keys(initialData).length > 0) {
            setFormData({
                fullName: initialData.fullName || '',
                contactID: initialData.contactID || '',
                address: initialData.address || '',
                city: initialData.city || '',
                postalCode: initialData.postalCode || '',
                province: initialData.province || ''
            });
        }
    }, [initialData]);

    // Validar si el formulario está completo
    useEffect(() => {
        const isValid = Object.values(formData).every(value => value.trim() !== '');
        setIsFormValid(isValid);
    }, [formData]);

    // Manejar cambios en los inputs
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Manejar creación de factura simplificada
    const handleCreateSimplified = () => {
        if (onCreateSimplified) {
            onCreateSimplified();
        }
    };

    // Manejar creación de factura personalizada
    const handleCreateCustom = () => {
        if (onCreateCustom && isFormValid) {
            onCreateCustom(formData);
        }
    };

    // Resetear formulario al cerrar
    const handleClose = () => {
        setFormData({
            fullName: '',
            contactID: '',
            address: '',
            city: '',
            postalCode: '',
            province: ''
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <MainModal 
            isVisible={isOpen} 
            onClose={()=>handleClose()}
            title="Datos de Facturación"
        >
            <div style={style.container}>
                <div style={style.title}>
                    Introduce los datos de facturación
                </div>

                <div style={style.formRow}>
                    <label style={style.label}>Nombre y Apellido</label>
                    <input
                        style={style.input}
                        type="text"
                        value={formData.fullName}
                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                        placeholder="Introduce el nombre completo"
                    />
                </div>

                <div style={style.formRow}>
                    <label style={style.label}>NIF/CIF/VAT/DNI</label>
                    <input
                        style={style.input}
                        type="text"
                        value={formData.contactID}
                        onChange={(e) => handleInputChange('contactID', e.target.value)}
                        placeholder="Introduce el número de identificación fiscal"
                    />
                </div>

                <div style={style.formRow}>
                    <label style={style.label}>Dirección</label>
                    <input
                        style={style.input}
                        type="text"
                        value={formData.address}
                        onChange={(e) => handleInputChange('address', e.target.value)}
                        placeholder="Introduce la dirección"
                    />
                </div>

                <div style={style.formRowDouble}>
                    <div style={style.formColumn}>
                        <label style={style.label}>Ciudad</label>
                        <input
                            style={style.input}
                            type="text"
                            value={formData.city}
                            onChange={(e) => handleInputChange('city', e.target.value)}
                            placeholder="Introduce la ciudad"
                        />
                    </div>
                    <div style={style.formColumn}>
                        <label style={style.label}>Código Postal</label>
                        <input
                            style={style.input}
                            type="text"
                            value={formData.postalCode}
                            onChange={(e) => handleInputChange('postalCode', e.target.value)}
                            placeholder="Introduce el código postal"
                        />
                    </div>
                </div>

                <div style={style.formRow}>
                    <label style={style.label}>Provincia</label>
                    <input
                        style={style.input}
                        type="text"
                        value={formData.province}
                        onChange={(e) => handleInputChange('province', e.target.value)}
                        placeholder="Introduce la provincia"
                    />
                </div>

                <div style={style.buttonContainer}>
                    <button
                        style={{
                            ...style.button,
                            ...style.buttonSecondary
                        }}
                        onClick={handleCreateSimplified}
                        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                        onMouseLeave={(e) => e.target.style.opacity = '1'}
                    >
                        Crear Factura Simplificada
                    </button>
                    
                    <button
                        style={{
                            ...style.button,
                            ...(isFormValid ? style.buttonPrimary : style.buttonDisabled)
                        }}
                        onClick={handleCreateCustom}
                        disabled={!isFormValid}
                        onMouseEnter={(e) => {
                            if (isFormValid) e.target.style.opacity = '0.8';
                        }}
                        onMouseLeave={(e) => {
                            if (isFormValid) e.target.style.opacity = '1';
                        }}
                    >
                        Crear Factura Personalizada
                    </button>
                </div>
            </div>
        </MainModal>
    );
} 